import { useState, useEffect } from 'react';
import { Edit, useRecordContext, useDataProvider, useNotify, useRefresh, useGetList } from 'react-admin';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField,
  CircularProgress, Collapse, FormGroup, FormControlLabel, Checkbox,
  Table, TableHead, TableRow, TableCell, TableBody, Divider,
} from '@mui/material';
import EditIcon  from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon  from '@mui/icons-material/Save';
import authFetch from '../utils/authFetch';
import { card, btn, inputSx, NAVY, typo } from '../styles/ds';

const fmt = (v, cur) => cur === 'USD'
  ? `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  : `₪${Number(v || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

const WorkerEditContent = () => {
  const record  = useRecordContext();
  const dp      = useDataProvider();
  const notify  = useNotify();
  const refresh = useRefresh();

  const { data: roles = [] } = useGetList('worker_roles', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  // ── Collapsed details/roles editor ──────────────────────────────────────────
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [name,    setName]    = useState('');
  const [phone,   setPhone]   = useState('');
  const [roleIds, setRoleIds] = useState([]);
  const [notes,   setNotes]   = useState('');
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (!record) return;
    setName(record.name || '');
    setPhone(record.phone || '');
    setRoleIds(record.role_ids || []);
    setNotes(record.notes || '');
  }, [record]);

  const toggleRole = id => setRoleIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const handleSave = async () => {
    if (!name.trim()) { notify('נא להזין שם עובד', { type: 'warning' }); return; }
    setSaving(true);
    try {
      await dp.update('workers', {
        id: record.id,
        data: { name: name.trim(), phone: phone || null, role_ids: roleIds, notes: notes || null },
        previousData: record,
      });
      notify('פרטי העובד עודכנו', { type: 'success' });
      setDetailsOpen(false);
      refresh();
    } catch (e) {
      notify(e?.body?.error || e?.message || 'שגיאה בשמירה', { type: 'error' });
    } finally { setSaving(false); }
  };

  // ── Cost summary (read-only) ────────────────────────────────────────────────
  const [costs, setCosts]               = useState([]);
  const [costsLoading, setCostsLoading] = useState(true);

  useEffect(() => {
    if (!record) return;
    setCostsLoading(true);
    authFetch(`/production_costs?_start=0&_end=500&_sort=work_date&_order=DESC&filter=${encodeURIComponent(JSON.stringify({ worker_id: record.id }))}`)
      .then(r => r.json())
      .then(d => { setCosts(Array.isArray(d) ? d : []); setCostsLoading(false); })
      .catch(() => setCostsLoading(false));
  }, [record]);

  // production_costs.cost is a per-unit rate — total_cost (from the API,
  // already multiplied by the batch's quantity) is what actually reflects
  // what this work cost the business.
  const totalIls = costs.filter(c => c.currency === 'ILS').reduce((s, c) => s + Number(c.total_cost ?? c.cost ?? 0), 0);
  const totalUsd = costs.filter(c => c.currency === 'USD').reduce((s, c) => s + Number(c.total_cost ?? c.cost ?? 0), 0);

  if (!record) return (
    <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
  );

  return (
    <Box className="crm-page crm-detail-content" p={3} sx={{ width: '100%', boxSizing: 'border-box' }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography {...typo.pageTitle}>{record.name}</Typography>
        <Button
          variant={detailsOpen ? 'outlined' : 'contained'}
          size="small"
          startIcon={detailsOpen ? <CloseIcon /> : <EditIcon />}
          onClick={() => setDetailsOpen(o => !o)}
          sx={detailsOpen ? { borderColor: '#c8a96e', color: '#c8a96e', borderRadius: 2 } : btn.primary}
        >
          {detailsOpen ? 'סגור' : 'ערוך פרטים'}
        </Button>
      </Stack>

      {/* ── Collapsed details/roles editor ─────────────────────────────────── */}
      <Collapse in={detailsOpen} timeout={250} unmountOnExit>
        <Card sx={{ ...card.section, mb: 3 }}>
          <CardContent className="crm-card-content" sx={{ p: 3 }}>
            <Typography fontWeight={700} color={NAVY} mb={2}>פרטים ותפקידים</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField label="שם העובד" value={name} onChange={e => setName(e.target.value)} fullWidth sx={inputSx} />
              <TextField label="טלפון" value={phone} onChange={e => setPhone(e.target.value)} fullWidth sx={inputSx} />
            </Box>
            <Typography fontSize={13} fontWeight={600} color="text.secondary" mb={1}>תפקידים</Typography>
            <FormGroup row sx={{ mb: 2 }}>
              {roles.map(r => (
                <FormControlLabel key={r.id} label={r.name}
                  control={<Checkbox checked={roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />} />
              ))}
            </FormGroup>
            <TextField label="הערות" value={notes} onChange={e => setNotes(e.target.value)} multiline rows={3} fullWidth sx={inputSx} />
            <Stack className="crm-actions" direction="row" justifyContent="flex-end" mt={2}>
              <Button variant="contained" onClick={handleSave} disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={btn.primary}>
                שמור שינויים
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Collapse>

      {/* ── Cost summary ─────────────────────────────────────────────────── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color={NAVY} mb={2}>סיכום עבודות ועלויות</Typography>
          {costsLoading ? <CircularProgress size={20} /> : costs.length === 0 ? (
            <Typography color="text.secondary" fontSize={13} py={2} textAlign="center">
              אין עלויות ייצור רשומות לעובד זה
            </Typography>
          ) : (
            <>
              <Box className="crm-record-scroll" data-mobile-scroll sx={{ overflowX: 'auto' }}>
                <Table className="crm-record-table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>מוצר</TableCell>
                      <TableCell>סוג עבודה</TableCell>
                      <TableCell align="right">עלות</TableCell>
                      <TableCell align="center">תאריך</TableCell>
                      <TableCell>הערות</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {costs.map((c, i) => (
                      <TableRow className="crm-record-row" key={c.id} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#faf8f5' }}>
                        <TableCell className="crm-record-title crm-record-full" sx={{ fontSize: 13 }}>{c.variant_name || '—'}</TableCell>
                        <TableCell className="crm-record-full" data-label="סוג עבודה" sx={{ fontSize: 13 }}>{c.work_type || '—'}</TableCell>
                        <TableCell data-label="עלות" align="right" sx={{ fontWeight: 600, color: NAVY }}>
                          {fmt(c.total_cost ?? c.cost, c.currency)}
                          {c.batch_quantity > 1 && (
                            <Typography component="div" fontSize={10} color="text.secondary" fontWeight={400}>
                              {fmt(c.cost, c.currency)} × {c.batch_quantity} יח'
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell data-label="תאריך" align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>{fmtDate(c.work_date)}</TableCell>
                        <TableCell className="crm-record-full" data-label="הערות" sx={{ fontSize: 12, color: 'text.secondary' }}>{c.notes || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Stack className="crm-actions" direction="row" justifyContent="flex-end" spacing={3}>
                {totalIls > 0 && (
                  <Box textAlign="center">
                    <Typography variant="caption" color="text.secondary">סה"כ ₪</Typography>
                    <Typography fontWeight={700} fontSize={18} color={NAVY}>{fmt(totalIls, 'ILS')}</Typography>
                  </Box>
                )}
                {totalUsd > 0 && (
                  <Box textAlign="center">
                    <Typography variant="caption" color="text.secondary">סה"כ $</Typography>
                    <Typography fontWeight={700} fontSize={18} color={NAVY}>{fmt(totalUsd, 'USD')}</Typography>
                  </Box>
                )}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const WorkerEdit = () => (
  <Edit title=" " actions={false}>
    <WorkerEditContent />
  </Edit>
);

export default WorkerEdit;
